import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Badge,
  Box,
} from '@mui/material';
import { AppHeader } from '../components/app-header';
import { LoadingState } from '../components/loading-state';
import {
  useGetAvailableFeaturesQuery,
  useGetFeatureStatsQuery,
  useGetFeatureByOrganizationQuery,
} from '../store/api';

export default function FeatureExplorerPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');

  const selectedFeature = searchParams.get('feature') || null;

  // Fetch all available features
  const {
    data: featuresData,
    isLoading: featuresLoading,
    error: featuresError,
  } = useGetAvailableFeaturesQuery();

  // Fetch feature stats (only when a feature is selected)
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useGetFeatureStatsQuery(
    { featureName: selectedFeature || '' },
    { skip: !selectedFeature }
  );

  // Fetch feature by organization (only when a feature is selected)
  const {
    data: orgData,
    isLoading: orgLoading,
    error: orgError,
  } = useGetFeatureByOrganizationQuery(
    { featureName: selectedFeature || '' },
    { skip: !selectedFeature }
  );

  const features = featuresData?.feature_flags || [];
  const filteredFeatures = useMemo(() => {
    if (!searchInput.trim()) return features;
    const searchLower = searchInput.toLowerCase();
    return features.filter((feature: string) =>
      feature.toLowerCase().includes(searchLower)
    );
  }, [features, searchInput]);

  const handleFeatureSelect = useCallback(
    (featureName: string) => {
      const params = new URLSearchParams();
      params.set('feature', featureName);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOrgClick = useCallback(
    (organizationId: string) => {
      navigate(`/admin-v2/organization/${organizationId}`);
    },
    [navigate]
  );

  const enabledCount = statsData?.enabled_count || 0;
  const disabledCount = statsData?.disabled_count || 0;
  const totalUsers = enabledCount + disabledCount;
  const enabledPercent = totalUsers > 0 ? (enabledCount / totalUsers) * 100 : 0;
  const disabledPercent = totalUsers > 0 ? (disabledCount / totalUsers) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Feature Explorer</h1>
          <p className="text-gray-600 mt-1">
            Explore feature flags and their usage across organizations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Feature List */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardContent>
                <Typography variant="h6" className="mb-4">
                  Features
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search features..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="mb-4"
                />
                {featuresLoading ? (
                  <div className="flex justify-center p-4">
                    <CircularProgress size={24} />
                  </div>
                ) : featuresError ? (
                  <div className="p-4 text-red-600 text-sm">
                    Error loading features. Please try again.
                  </div>
                ) : filteredFeatures.length === 0 ? (
                  <div className="p-4 text-gray-500 text-sm">
                    {searchInput ? 'No features found' : 'No features available'}
                  </div>
                ) : (
                  <List className="max-h-[calc(100vh-300px)] overflow-y-auto">
                    {filteredFeatures.map((feature: string) => (
                      <ListItem key={feature} disablePadding>
                        <ListItemButton
                          selected={selectedFeature === feature}
                          onClick={() => handleFeatureSelect(feature)}
                        >
                          <ListItemText
                            primary={feature}
                           slotProps={{ primary: {
                              className: selectedFeature === feature ? 'font-medium' : '',
                            } }}/>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Feature Detail */}
          <div className="lg:col-span-2">
            {!selectedFeature ? (
              <Card>
                <CardContent>
                  <Typography variant="body1" className="text-gray-500 text-center py-8">
                    Select a feature from the list to view details
                  </Typography>
                </CardContent>
              </Card>
            ) : statsLoading || orgLoading ? (
              <LoadingState message="Loading feature details..." />
            ) : statsError || orgError ? (
              <Card>
                <CardContent>
                  <Typography variant="body1" className="text-red-600">
                    Error loading feature details. Please try again.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Stats Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" className="mb-4">
                      {selectedFeature}
                    </Typography>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <Typography variant="body2" className="text-gray-600 mb-1">
                          Enabled
                        </Typography>
                        <Typography variant="h4" className="text-green-700 font-bold">
                          {enabledCount}
                        </Typography>
                        <Typography variant="body2" className="text-gray-500 mt-1">
                          {enabledPercent.toFixed(1)}%
                        </Typography>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <Typography variant="body2" className="text-gray-600 mb-1">
                          Disabled
                        </Typography>
                        <Typography variant="h4" className="text-red-700 font-bold">
                          {disabledCount}
                        </Typography>
                        <Typography variant="body2" className="text-gray-500 mt-1">
                          {disabledPercent.toFixed(1)}%
                        </Typography>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Typography variant="body2" className="text-gray-600">
                        Total Users: <span className="font-medium">{totalUsers}</span>
                      </Typography>
                    </div>
                  </CardContent>
                </Card>

                {/* Organizations Table */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" className="mb-4">
                      Organizations
                    </Typography>
                    {orgData && orgData.organizations.length > 0 ? (
                      <>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Organization Name</TableCell>
                              <TableCell align="right">Users</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orgData.organizations.map(org => (
                              <TableRow
                                key={org.organization_id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleOrgClick(org.organization_id)}
                              >
                                <TableCell>
                                  {org.organization_name || (
                                    <span className="text-gray-400 italic">
                                      {org.organization_id}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Badge color="primary" badgeContent={org.user_count}>
                                    <span />
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {orgData.users_without_organization > 0 && (
                          <Box className="mt-4 p-3 bg-gray-50 rounded">
                            <Typography variant="body2" className="text-gray-600">
                              Users without organization:{' '}
                              <span className="font-medium">
                                {orgData.users_without_organization}
                              </span>
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" className="text-gray-500 py-4">
                        No organizations found for this feature
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
